import React from 'react';

// SVG renderer for a screening spec's declarative logic tree (served by the
// backend as typeMeta.logicTree — see backend lib/generation/specs/grid-tree).
// Node x/y are grid units; we just scale them. `highlightPath` (the gate ids
// from outcome.path) lights up the route a finished screening actually took;
// terminal stops mid-tree get a synthetic dashed hop to the outcome node.

const CELL_W = 168;
const CELL_H = 86;
const NODE_W = 148;
const NODE_H = 44;
const PAD = 12;

const SEVERITY_COLOR = {
  success: 'var(--color-green-500, #16a34a)',
  warning: 'var(--color-yellow-500)',
  critical: 'var(--color-red-500, #dc2626)',
};

function nodeCenter(node) {
  return {
    x: PAD + node.x * CELL_W + NODE_W / 2,
    y: PAD + node.y * CELL_H + NODE_H / 2,
  };
}

// Wrap a label into up to two lines that fit the node box.
function wrapLabel(label, maxChars = 22) {
  const words = String(label).split(' ');
  const lines = [''];
  for (const w of words) {
    const cur = lines[lines.length - 1];
    if (cur && (cur + ' ' + w).length > maxChars && lines.length < 2) lines.push(w);
    else lines[lines.length - 1] = cur ? `${cur} ${w}` : w;
  }
  return lines;
}

export default function LogicTree({ tree, highlightPath = null, outcome = null }) {
  if (!tree?.nodes?.length) return null;
  const nodes = new Map(tree.nodes.map((n) => [n.id, n]));
  const width = PAD * 2 + (Math.max(...tree.nodes.map((n) => n.x)) + 1) * CELL_W - (CELL_W - NODE_W);
  const height = PAD * 2 + (Math.max(...tree.nodes.map((n) => n.y)) + 1) * CELL_H - (CELL_H - NODE_H) + 4;

  const path = highlightPath || [];
  const highlighted = new Set(path);
  const hasHighlight = path.length > 0;
  if (hasHighlight) highlighted.add('outcome');

  // Edges on the taken route; plus a synthetic hop when the screening
  // terminated at a gate with no declared edge to the outcome node.
  const litEdges = new Set();
  const fullRoute = hasHighlight ? [...path, 'outcome'] : [];
  const synthetic = [];
  for (let i = 1; i < fullRoute.length; i++) {
    const key = `${fullRoute[i - 1]}->${fullRoute[i]}`;
    if (tree.edges.some((e) => e.from === fullRoute[i - 1] && e.to === fullRoute[i])) {
      litEdges.add(key);
    } else {
      synthetic.push({ from: fullRoute[i - 1], to: fullRoute[i] });
    }
  }

  const outcomeColor = outcome ? SEVERITY_COLOR[outcome.severity] || 'var(--color-purple-500)' : null;

  const renderEdge = (edge, idx, dashed = false) => {
    const from = nodes.get(edge.from);
    const to = nodes.get(edge.to);
    if (!from || !to) return null;
    const a = nodeCenter(from);
    const b = nodeCenter(to);
    const lit = dashed || litEdges.has(`${edge.from}->${edge.to}`);
    const dim = hasHighlight && !lit;
    const start = { x: a.x, y: a.y + NODE_H / 2 };
    const end = { x: b.x, y: b.y - NODE_H / 2 };
    const midY = (start.y + end.y) / 2;
    const d = `M ${start.x} ${start.y} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${end.y}`;
    return (
      <g key={`${edge.from}-${edge.to}-${idx}`} opacity={dim ? 0.3 : 1}>
        <path
          d={d}
          fill="none"
          stroke={lit && hasHighlight ? 'var(--color-purple-500)' : 'var(--border-primary)'}
          strokeWidth={lit && hasHighlight ? 2 : 1.25}
          strokeDasharray={dashed ? '4 4' : undefined}
        />
        {edge.label && (
          <text
            x={(start.x + end.x) / 2}
            y={midY - 4}
            textAnchor="middle"
            fontSize="9"
            fill="var(--text-tertiary)"
          >
            {edge.label}
          </text>
        )}
      </g>
    );
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', maxWidth: width, display: 'block' }}
        role="img"
        aria-label="Screening logic tree"
      >
        {tree.edges.map((e, i) => renderEdge(e, i))}
        {synthetic.map((e, i) => renderEdge(e, `s${i}`, true))}
        {tree.nodes.map((n) => {
          const lit = highlighted.has(n.id);
          const dim = hasHighlight && !lit;
          const isOutcome = n.kind === 'outcome';
          const stroke = isOutcome && outcomeColor && lit
            ? outcomeColor
            : lit && hasHighlight ? 'var(--color-purple-500)' : 'var(--border-primary)';
          const label = isOutcome && outcome
            ? `${outcome.outcome === 'proceed' ? 'Generate package' : outcome.outcome.charAt(0).toUpperCase() + outcome.outcome.slice(1)}`
            : n.label;
          const lines = wrapLabel(label);
          const x = PAD + n.x * CELL_W;
          const y = PAD + n.y * CELL_H;
          return (
            <g key={n.id} opacity={dim ? 0.35 : 1}>
              <rect
                x={x}
                y={y}
                width={NODE_W}
                height={NODE_H}
                rx="8"
                fill="var(--surface-primary, #fff)"
                stroke={stroke}
                strokeWidth={lit && hasHighlight ? 2 : 1}
              />
              {lines.map((line, li) => (
                <text
                  key={li}
                  x={x + NODE_W / 2}
                  y={y + NODE_H / 2 + (li - (lines.length - 1) / 2) * 12 + 3.5}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight={lit && hasHighlight ? 600 : 450}
                  fill={isOutcome && outcomeColor && lit ? outcomeColor : 'var(--text-primary)'}
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
      {tree.note && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
          {tree.note}
        </div>
      )}
    </div>
  );
}
